/**
 * 自己登録フォーム（/signup）: 郵便番号から住所（都道府県・市区町村・町域）を
 * 検索し、該当フィールドへ自動入力するボタン。
 *
 * 郵便番号入力のたびに自動発火する方式（YubinBango標準のmicroformats2
 * クラス監視によるバインディング）ではなく、ボタンクリックを起点にする
 * 明示的なUIとして実装している。
 *
 * データソース: https://yubinbango.github.io/yubinbango-data/data/{郵便番号先頭3桁}.js
 * （YubinBango: https://yubinbango.github.io/yubinbango/ が参照しているのと同じ
 * データ配信元。実データを取得して確認済み・現行で稼働している）。
 *
 * レスポンスは `$yubin({"1000001":[13,"千代田区","千代田"], ...});` という
 * JSONP形式（本来はscriptタグ挿入 + コールバック関数`$yubin`のグローバル定義で
 * 読み込む想定のもの）。ただしこの配信元はGitHub Pages上の静的ファイルであり、
 * レスポンスヘッダーに `Access-Control-Allow-Origin: *` が付与されている
 * （実際に確認済み）ため、scriptタグ経由でリモートJSを実行する必要はなく、
 * fetch()でテキストとして取得し、`$yubin(...)`の括弧内をJSON.parseする方式にした。
 * こちらの方が、任意のリモートスクリプトをそのまま実行するJSONP方式より安全で、
 * テストも書きやすい。
 *
 * 各エントリの配列は [都道府県コード(1〜47), 市区町村名, 町域名, (町域内詳細)]。
 * 都道府県コード→都道府県名の変換テーブル(REGION)はyubinbango-core.jsに埋め込まれて
 * いるものと同じ内容（配信データ側にはテーブル定義は含まれないため、呼び出し側で
 * 保持する必要がある）。
 *
 * 番地フィールド（address_txt）には町域名までを入れる。取得できるデータの粒度は
 * 「町域」までが基本（一部、大口事業所等で町域内詳細まで含む場合があるが、番地・
 * 建物名相当の情報のため、本実装では取り込まずユーザーの手入力に委ねる）。
 */
document.addEventListener('DOMContentLoaded', () => {
    const form = document.getElementById('signup-form');
    const button = document.getElementById('postal-code-search-button');
    if (!form || !button) {
        return;
    }
    // 二重バインド防止（このボタンに対して既にイベントを登録済みならスキップする）。
    if (button.dataset.postalSearchBound === '1') {
        return;
    }
    button.dataset.postalSearchBound = '1';

    const postNumField = form.elements['post_num'];
    const prefectureField = form.elements['prefecture_txt'];
    const cityField = form.elements['city_txt'];
    const addressField = form.elements['address_txt'];
    const errorEl = document.getElementById('postal-code-search-error');

    const DATA_BASE_URL = 'https://yubinbango.github.io/yubinbango-data/data';
    const REQUEST_TIMEOUT_MS = 8000;
    const DEFAULT_BUTTON_TEXT = button.textContent.trim();

    // 都道府県コード(1〜47)→都道府県名。インデックス0はダミー（コードが1始まりのため）。
    const REGION = [
        null, '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
        '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
        '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
        '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
        '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
        '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
        '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
    ];

    function showError(message) {
        if (!errorEl) {
            return;
        }
        errorEl.textContent = message;
        errorEl.classList.toggle('d-none', message === '');
    }

    function setLoading(loading) {
        button.disabled = loading;
        button.textContent = loading ? '検索中…' : DEFAULT_BUTTON_TEXT;
    }

    // フィールドへ値を設定したうえで、クライアント側バリデーション
    // （signup-create.jsのbindField）が反映されるようinputイベントを発火する。
    function applyFieldValue(field, value) {
        if (!field) {
            return;
        }
        field.value = value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // 全角数字・全角/各種ハイフンを許容し、半角数字7桁に正規化する。
    // 正規化しても7桁にならない場合は空文字を返す。
    function normalizePostalCode(raw) {
        const halfWidth = raw
            .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))
            .replace(/[－ー―‐−]/g, '-');
        const digitsOnly = halfWidth.replace(/[-\s]/g, '');
        return /^\d{7}$/.test(digitsOnly) ? digitsOnly : '';
    }

    function applyAddress(entry) {
        const [prefectureCode, city, town] = entry;
        applyFieldValue(prefectureField, REGION[prefectureCode] || '');
        applyFieldValue(cityField, city || '');
        applyFieldValue(addressField, town || '');
    }

    async function searchAddress() {
        showError('');

        const normalized = normalizePostalCode(postNumField ? postNumField.value || '' : '');
        if (!normalized) {
            showError('郵便番号は7桁で入力してください');
            return;
        }

        setLoading(true);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

        try {
            const first3 = normalized.slice(0, 3);
            const response = await fetch(`${DATA_BASE_URL}/${first3}.js`, { signal: controller.signal });
            if (!response.ok) {
                showError('住所の検索に失敗しました。しばらくしてから再度お試しください');
                return;
            }

            const text = await response.text();
            const match = text.match(/^\$yubin\((.*)\);?\s*$/s);
            if (!match) {
                showError('住所の検索に失敗しました。しばらくしてから再度お試しください');
                return;
            }

            let data;
            try {
                data = JSON.parse(match[1]);
            } catch {
                showError('住所の検索に失敗しました。しばらくしてから再度お試しください');
                return;
            }

            const entry = data[normalized];
            if (!entry) {
                showError('該当する住所が見つかりませんでした');
                return;
            }

            applyAddress(entry);
        } catch (error) {
            showError(error && error.name === 'AbortError'
                ? '通信がタイムアウトしました。しばらくしてから再度お試しください'
                : '住所の検索に失敗しました。しばらくしてから再度お試しください');
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }

    button.addEventListener('click', () => {
        searchAddress();
    });
});
